import React, { useEffect } from 'react';

import { connect } from 'react-redux';
import { Form, Button, Card } from 'react-bootstrap';
import { toast } from 'react-toastify';

import { PageHeader, Loading } from '../../components';

import useComponentState from '../../utils/useComponentState';
import { createLoyaltyPointRatio } from '../../api/loyaltyPointRatio';
import { getAllLoyaltyTiers } from '../../api/tier';
import {
    INITIAL_STATE,
    TOAST_MESSAGES,
    VALIDATION_ERROR_MESSAGES,
} from './addMccCodeConstants';

import styles from './addMccCode.module.scss';

const validateAllFields = (state) => {
    const errors = {};

    if (!state.ratioType || state.ratioType.trim().length === 0) {
        errors.ratioType = VALIDATION_ERROR_MESSAGES.ratioType;
    } else if (!/^\d+$/.test(state.ratioType.trim())) {
        errors.ratioType = VALIDATION_ERROR_MESSAGES.ratioTypeNumeric;
    }

    const accRatio = parseFloat(state.accumulationRatio);
    if (state.accumulationRatio === '' || isNaN(accRatio) || accRatio <= 0) {
        errors.accumulationRatio = VALIDATION_ERROR_MESSAGES.accumulationRatio;
    }

    if (!state.loyaltyTierId) {
        errors.loyaltyTierId = VALIDATION_ERROR_MESSAGES.loyaltyTierId;
    }

    return { isValid: Object.keys(errors).length === 0, errors };
};

const createPayload = (state) => ({
    ratioType: state.ratioType.trim(),
    accumulationRatio: parseFloat(state.accumulationRatio),
    loyaltyTierId: parseInt(state.loyaltyTierId, 10),
});

const AddMccCode = ({ auth }) => {
    const [state, setState] = useComponentState(INITIAL_STATE);

    useEffect(() => {
        loadTiers();
    }, []);

    const loadTiers = async () => {
        setState({ tiersLoading: true });
        try {
            const tiers = await getAllLoyaltyTiers(auth.auth.token);
            setState({ tiers: tiers || [] });
        } catch {
            toast.error('Failed to load loyalty tiers.');
        }
        setState({ tiersLoading: false });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const nextValue = name === 'ratioType' ? value.replace(/\D/g, '') : value;
        setState((prev) => ({
            ...prev,
            [name]: nextValue,
            fieldErrors: { ...prev.fieldErrors, [name]: '' },
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const { isValid, errors } = validateAllFields(state);
        if (!isValid) {
            setState((prev) => ({ ...prev, fieldErrors: errors }));
            return;
        }

        setState((prev) => ({ ...prev, loading: true, error: '' }));

        try {
            const payload = createPayload(state);
            await createLoyaltyPointRatio(auth.auth.token, payload);
            toast.success(TOAST_MESSAGES.success);
            setState({ ...INITIAL_STATE, tiers: state.tiers });
        } catch (error) {
            const errorMessage = error.response?.data?.message || TOAST_MESSAGES.error;
            setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
            toast.error(errorMessage);
        }
    };

    const handleCancel = () => setState({ ...INITIAL_STATE, tiers: state.tiers });

    return (
        <div className={styles.root_div}>
            <PageHeader text='Add MCC Codes' />

            <Card className={styles.formCard}>
                <Card.Body>
                    <Form noValidate onSubmit={handleSubmit}>
                        <div className='mb-4'>
                            <small className='text-muted'>
                                Fields marked with <span className='text-danger'>*</span> are required
                            </small>
                        </div>

                        <div className={styles.sectionContainer}>
                            <div className={styles.sectionTitle}>Ratio Details</div>

                            <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel}>
                                    MCC Code <span className='text-danger'>*</span>
                                </label>
                                <Form.Control
                                    type='text'
                                    name='ratioType'
                                    value={state.ratioType}
                                    onChange={handleInputChange}
                                    placeholder='e.g. 1111, 2222'
                                    isInvalid={!!state.fieldErrors?.ratioType}
                                />
                                <Form.Control.Feedback type='invalid' style={{ display: 'block' }}>
                                    {state.fieldErrors?.ratioType}
                                </Form.Control.Feedback>
                            </div>

                            <div className={styles.fieldRow}>
                                <div className={styles.fieldGroup}>
                                    <label className={styles.fieldLabel}>
                                        Accumulation Ratio <span className='text-danger'>*</span>
                                    </label>
                                    <Form.Control
                                        type='number'
                                        name='accumulationRatio'
                                        value={state.accumulationRatio}
                                        onChange={handleInputChange}
                                        placeholder='e.g. 0.002500'
                                        step='any'
                                        min='0.01'
                                        isInvalid={!!state.fieldErrors?.accumulationRatio}
                                    />
                                    <Form.Control.Feedback type='invalid' style={{ display: 'block' }}>
                                        {state.fieldErrors?.accumulationRatio}
                                    </Form.Control.Feedback>
                                </div>

                            </div>

                            <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel}>
                                    Loyalty Tier <span className='text-danger'>*</span>
                                </label>
                                <Form.Control
                                    as='select'
                                    name='loyaltyTierId'
                                    value={state.loyaltyTierId}
                                    onChange={handleInputChange}
                                    isInvalid={!!state.fieldErrors?.loyaltyTierId}
                                    disabled={state.tiersLoading}
                                >
                                    <option value=''>
                                        {state.tiersLoading ? 'Loading tiers...' : 'Select a tier'}
                                    </option>
                                    {state.tiers.map((tier) => (
                                        <option key={tier.id} value={tier.id}>
                                            {tier.name}
                                        </option>
                                    ))}
                                </Form.Control>
                                <Form.Control.Feedback type='invalid' style={{ display: 'block' }}>
                                    {state.fieldErrors?.loyaltyTierId}
                                </Form.Control.Feedback>
                            </div>
                        </div>

                        <div className={styles.formActions}>
                            <Button variant='secondary' type='button' onClick={handleCancel}>
                                Cancel
                            </Button>
                            <Button type='submit' variant='primary' disabled={state.loading}>
                                {state.loading ? 'Adding...' : 'Add MCC Code'}
                            </Button>
                        </div>
                    </Form>

                    {state.loading && <Loading />}
                </Card.Body>
            </Card>
        </div>
    );
};

const mapStateToProps = (state) => ({ auth: state.auth });

export default connect(mapStateToProps)(AddMccCode);
